/* Progressive reading helpers. Preserve the wording and links of merchant documents. */
(() => {
  'use strict';
  function formatPolicySections(content) {
    // Some Shopify policy editors store the entire document in one paragraph.
    // Only format the known section-heading pattern; leave other documents alone.
    if (!content.closest('.shopify-policy__body') || !/ABSCHNITT\s+\d+\s*[–—:-]/i.test(content.textContent)) return;
    [...content.children].filter(el => el.tagName === 'P').forEach(paragraph => {
      const children = [...paragraph.childNodes];
      if (children.filter(node => node.nodeName === 'BR').length < 3) return;
      if (paragraph.querySelector('input, button, textarea, select, img, iframe')) return;
      const replacement = document.createDocumentFragment();
      let line = document.createDocumentFragment();
      let first = true;
      function flush() {
        const text = line.textContent.trim().replace(/\s+/g, ' ');
        if (!text) return;
        const isHeading = text.length < 180 && /^(?:ABSCHNITT\s+\d+\s*[–—:-].+|ÜBERSICHT)$/i.test(text);
        const previous = replacement.lastElementChild;
        // Keep a sentence together when an editor inserted a soft line break.
        if (!isHeading && previous?.tagName === 'P' && !/[.!?:;”"»)]$/.test(previous.textContent.trim()) && /^[a-zäöüß]/.test(text)) {
          previous.append(document.createTextNode(' '), line);
          line = document.createDocumentFragment();
          return;
        }
        const block = document.createElement(isHeading ? 'h2' : 'p');
        for (const attr of paragraph.attributes) {
          if (attr.name !== 'id' || first) block.setAttribute(attr.name, attr.value);
        }
        block.append(line);
        replacement.append(block);
        line = document.createDocumentFragment();
        first = false;
      }
      children.forEach(node => {
        if (node.nodeName === 'BR') flush();
        else line.append(node.cloneNode(true));
      });
      flush();
      if (replacement.childElementCount > 1) paragraph.replaceWith(replacement);
    });
  }
  function restorePolicyLinks(content, panel) {
    const privacyUrl = panel.dataset.kmPrivacyUrl;
    const refundUrl = panel.dataset.kmRefundUrl;
    if (!content.closest('.shopify-policy__body') || (!privacyUrl && !refundUrl)) return;
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) if (!walker.currentNode.parentElement.closest('a, script, style')) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      const text = node.nodeValue;
      const matches = [...text.matchAll(/(Datenschutzerklärung|Widerrufsrechts?)(,\s*die\s+hier)?\s*\[LINK\]/g)];
      if (!matches.length) return;
      const replacement = document.createDocumentFragment();
      let end = 0;
      matches.forEach(match => {
        const url = match[1] === 'Datenschutzerklärung' ? privacyUrl : refundUrl;
        if (!url) return;
        replacement.append(document.createTextNode(text.slice(end, match.index)));
        const link = document.createElement('a');
        link.href = url;
        link.textContent = match[1];
        replacement.append(link);
        if (match[2]) replacement.append(document.createTextNode(match[2]));
        end = match.index + match[0].length;
      });
      if (end) {
        replacement.append(document.createTextNode(text.slice(end)));
        node.replaceWith(replacement);
      }
    });
  }
  function enhanceDocuments(scope = document) {
    scope.querySelectorAll('[data-km-document]').forEach((panel) => {
      if (panel.dataset.kmEnhanced) return;
      const content = panel.querySelector('.shopify-policy__body .rte') || panel.querySelector('.shopify-policy__body') || panel.querySelector('.km-document-content');
      if (!content) return;
      panel.dataset.kmEnhanced = 'true';
      formatPolicySections(content);
      restorePolicyLinks(content, panel);

      const headings = [...content.querySelectorAll('h2, h3')].filter((heading) => heading.textContent.trim());
      if (headings.length >= 3) {
        const toc = document.createElement('details');
        toc.className = 'km-document-toc';
        const summary = document.createElement('summary');
        summary.textContent = 'Auf dieser Seite';
        const list = document.createElement('ol');
        headings.forEach((heading, index) => {
          if (!heading.id) {
            const slug = heading.textContent.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 70);
            let id = `km-abschnitt-${slug || index + 1}`;
            let suffix = 1;
            while (document.getElementById(id)) id = `km-abschnitt-${slug || index + 1}-${suffix++}`;
            heading.id = id;
          }
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = heading.textContent.trim();
          item.append(link);
          list.append(item);
        });
        toc.append(summary, list);
        const first = content.firstElementChild;
        if (first?.tagName === 'H1') first.after(toc);
        else content.prepend(toc);
      }

      content.querySelectorAll('table').forEach((table) => {
        let wrapper = table.parentElement;
        if (!wrapper.classList.contains('rte-table-wrapper') && !wrapper.classList.contains('km-document-table')) {
          wrapper = document.createElement('div');
          table.before(wrapper);
          wrapper.append(table);
        }
        wrapper.classList.add('km-document-table');
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || 'Tabelle – bei Bedarf seitlich scrollen');
      });

      const tools = document.createElement('div');
      tools.className = 'km-document-tools';
      const print = document.createElement('button');
      print.type = 'button';
      print.textContent = 'Seite drucken';
      print.addEventListener('click', () => window.print());
      tools.append(print);
      panel.append(tools);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhanceDocuments(), { once: true });
  else enhanceDocuments();
  document.addEventListener('shopify:section:load', (event) => enhanceDocuments(event.target));
})();
